import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Search, Package, History, X, Pencil } from "lucide-react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { companyProductSystemApiService } from "../../../infrastructure/api/company/company-product-system-api-service";
import type {
  MaterialDto,
  MaterialInventoryDto,
  MaterialInventoryLogDto,
  InventoryRequest,
} from "../../../domain/types/company/company-product-system";
import {
  MATERIAL_TYPE_LABELS,
  INVENTORY_CHANGE_TYPE_LABELS,
} from "../../../domain/types/company/company-product-system";

// ===== Type Badge Colors (브랜드 컬러 기반) =====
const TYPE_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  SHEET_PAPER: { bg: "#E8EFFF", text: "#4285F4" },
  ROLL_PAPER: { bg: "#E8EFFF", text: "#4285F4" },
  INK: { bg: "#f5f5f5", text: "#555" },
  INK_CONFIG: { bg: "#f5f5f5", text: "#555" },
  OTHER: { bg: "#f5f5f5", text: "#666" },
};

// ===== Inventory Change Modal =====
interface InventoryChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (request: InventoryRequest) => Promise<void>;
  type: "RECEIVE" | "DEDUCT" | "RETURN";
  materialName: string;
  currentQuantity: number;
}

const InventoryChangeModal: React.FC<InventoryChangeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  type,
  materialName,
  currentQuantity,
}) => {
  const [quantity, setQuantity] = useState<number>(0);
  const [memo, setMemo] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (quantity <= 0) {
      alert("수량을 입력해주세요.");
      return;
    }
    setLoading(true);
    try {
      await onSubmit({ quantity, memo: memo || undefined });
      onClose();
    } catch (e) {
      alert("처리에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case "RECEIVE": return "입고";
      case "DEDUCT": return "차감";
      case "RETURN": return "반품";
    }
  };

  const getNewQuantity = () => {
    switch (type) {
      case "RECEIVE": return currentQuantity + quantity;
      case "DEDUCT": return Math.max(0, currentQuantity - quantity);
      case "RETURN": return Math.max(0, currentQuantity - quantity);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40">
      <div className="w-[420px] rounded-[12px] bg-white p-6 shadow-lg">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[16px] font-bold text-[#222]">{getTitle()}</h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#666]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-[#f9f9f9] rounded-[8px]">
          <p className="text-[13px] text-[#666]">자재명</p>
          <p className="text-[15px] font-medium text-[#222]">{materialName}</p>
        </div>

        <div className="mb-4">
          <label className="block text-[13px] font-medium text-[#555] mb-1">
            현재 재고
          </label>
          <p className="text-[18px] font-bold text-[#222]">{currentQuantity.toLocaleString()}개</p>
        </div>

        <div className="mb-4">
          <label className="block text-[13px] font-medium text-[#555] mb-1">
            {type === "RECEIVE" ? "입고 수량" : type === "DEDUCT" ? "차감 수량" : "반품 수량"}
          </label>
          <input
            type="number"
            value={quantity || ""}
            onChange={(e) => setQuantity(Number(e.target.value))}
            className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[14px] focus:border-[#4285F4] focus:outline-none"
            placeholder="수량 입력"
            min={0}
          />
        </div>

        {quantity > 0 && (
          <div className="mb-4 p-3 bg-[#f0f4ff] rounded-[8px]">
            <p className="text-[12px] text-[#666]">변경 후 재고</p>
            <p className="text-[18px] font-bold text-[#4285F4]">{getNewQuantity().toLocaleString()}개</p>
          </div>
        )}

        <div className="mb-5">
          <label className="block text-[13px] font-medium text-[#555] mb-1">메모 (선택)</label>
          <textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[14px] focus:border-[#4285F4] focus:outline-none resize-none"
            placeholder="메모 입력"
            rows={2}
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] text-[#666] rounded-[8px] hover:bg-[#f5f5f5]"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || quantity <= 0}
            className="px-5 py-2 text-[13px] font-medium text-white bg-[#222] rounded-[8px] hover:bg-[#333] disabled:bg-[#ccc]"
          >
            {loading ? "처리중..." : "확인"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Inventory Log Modal =====
interface InventoryLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  materialName: string;
  materialId: number;
  companyId: number;
  logs: MaterialInventoryLogDto[];
  onUpdate: () => void;
}

const InventoryLogModal: React.FC<InventoryLogModalProps> = ({
  isOpen,
  onClose,
  materialName,
  materialId,
  companyId,
  logs,
  onUpdate,
}) => {
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [editQuantity, setEditQuantity] = useState(0);
  const [editMemo, setEditMemo] = useState("");
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const getChangeTypeStyle = (type: string) => {
    switch (type) {
      case "RECEIVE": return "bg-[#E8EFFF] text-[#4285F4]";
      case "DEDUCT": return "bg-[#f0f0f0] text-[#222]";
      case "RETURN": return "bg-[#f5f5f5] text-[#666]";
      default: return "bg-[#f5f5f5] text-[#666]";
    }
  };

  const startEdit = (log: MaterialInventoryLogDto) => {
    setEditingLogId(log.id);
    setEditQuantity(Math.abs(log.changeQuantity));
    setEditMemo(log.memo || "");
  };

  const cancelEdit = () => {
    setEditingLogId(null);
  };

  const handleSave = async (logId: number) => {
    if (editQuantity <= 0) {
      alert("수량은 양수여야 합니다.");
      return;
    }
    setSaving(true);
    try {
      await companyProductSystemApiService.updateInventoryLog(
        companyId, materialId, logId,
        { quantity: editQuantity, memo: editMemo || undefined },
      );
      setEditingLogId(null);
      onUpdate();
    } catch (error) {
      console.error("이력 수정 실패:", error);
      alert("이력 수정에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40">
      <div className="w-[700px] max-h-[80vh] rounded-[12px] bg-white shadow-lg overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-[#eee]">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#666]" />
            <h3 className="text-[16px] font-bold text-[#222]">재고 이력</h3>
          </div>
          <button onClick={onClose} className="text-[#999] hover:text-[#666]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 border-b border-[#eee] bg-[#f9f9f9]">
          <p className="text-[13px] text-[#666]">자재명</p>
          <p className="text-[15px] font-medium text-[#222]">{materialName}</p>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {logs.length === 0 ? (
            <div className="py-10 text-center text-[14px] text-[#999]">
              이력이 없습니다.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#fafafa] sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-[12px] font-medium text-[#888] text-center w-[150px]">일시</th>
                  <th className="px-3 py-3 text-[12px] font-medium text-[#888] text-center w-[70px]">유형</th>
                  <th className="px-3 py-3 text-[12px] font-medium text-[#888] text-center w-[70px]">변동</th>
                  <th className="px-3 py-3 text-[12px] font-medium text-[#888] text-center w-[70px]">변경 후</th>
                  <th className="px-3 py-3 text-[12px] font-medium text-[#888] text-center w-[180px]">메모</th>
                  <th className="px-2 py-3 text-[12px] font-medium text-[#888] text-center w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const isEditing = editingLogId === log.id;
                  return (
                    <tr key={log.id} className={`border-b border-[#f0f0f0] group/row ${isEditing ? "bg-[#f9f9f9]" : ""}`}>
                      <td className="px-4 py-3 text-[13px] text-[#555]">
                        {new Date(log.createdAt).toLocaleDateString("ko-KR", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-[11px] font-medium rounded-full ${getChangeTypeStyle(log.changeType)}`}>
                          {INVENTORY_CHANGE_TYPE_LABELS[log.changeType]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editQuantity || ""}
                            onChange={(e) => setEditQuantity(Number(e.target.value))}
                            className="w-[80px] border border-[#e0e0e0] rounded-[6px] px-2 py-1 text-[13px] text-right focus:border-[#4285F4] focus:outline-none"
                            min={1}
                          />
                        ) : (
                          <span className={`text-[13px] ${log.changeQuantity < 0 ? "text-[#888]" : "text-[#4285F4]"}`}>
                            {log.changeQuantity > 0 ? "+" : ""}{log.changeQuantity.toLocaleString()}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[13px] font-medium text-[#222] text-right">
                        {log.currentQuantity.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editMemo}
                            onChange={(e) => setEditMemo(e.target.value)}
                            className="w-full border border-[#e0e0e0] rounded-[6px] px-2 py-1 text-[13px] focus:border-[#4285F4] focus:outline-none"
                            placeholder="메모"
                          />
                        ) : (
                          <span className="text-[13px] text-[#888]">{log.memo || "-"}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isEditing ? (
                          <div className="flex items-center gap-1 justify-center">
                            <button
                              onClick={() => handleSave(log.id)}
                              disabled={saving}
                              className="px-2 py-1 text-[11px] bg-[#222] text-white rounded-[4px] hover:bg-[#333] disabled:bg-[#ccc]"
                            >
                              저장
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-2 py-1 text-[11px] text-[#666] border border-[#ddd] rounded-[4px] hover:bg-[#f5f5f5]"
                            >
                              취소
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(log)}
                            className="p-1.5 text-[#bbb] hover:text-[#555] hover:bg-[#f5f5f5] rounded-[4px] opacity-0 group-hover/row:opacity-100 transition-opacity"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-[#eee]">
          <button
            onClick={onClose}
            className="w-full py-2 text-[13px] text-[#666] bg-[#f5f5f5] rounded-[8px] hover:bg-[#eee]"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Main Page =====
const InventoryManagementPage: React.FC = () => {
  const { companyId } = useParams();
  const cid = Number(companyId);

  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const [inventoryList, setInventoryList] = useState<MaterialInventoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");

  // Modal states
  const [changeModal, setChangeModal] = useState<{
    isOpen: boolean;
    type: "RECEIVE" | "DEDUCT" | "RETURN";
    materialId: number;
    materialName: string;
    currentQuantity: number;
  } | null>(null);

  const [logModal, setLogModal] = useState<{
    isOpen: boolean;
    materialId: number;
    materialName: string;
    logs: MaterialInventoryLogDto[];
  } | null>(null);

  const loadData = useCallback(async () => {
    if (!Number.isFinite(cid)) return;
    setLoading(true);
    try {
      // 자재 목록 로드
      const materialsRes = await companyProductSystemApiService.getMaterials(cid);
      const loadedMaterials = materialsRes.data || [];
      setMaterials(loadedMaterials);

      // 재고 목록 로드
      const inventoryRes = await companyProductSystemApiService.getAllInventory(cid);
      setInventoryList(inventoryRes.data || []);
    } catch (e) {
      console.error(e);
      alert("데이터를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 자재 목록 기반으로 재고 데이터 매핑 (재고가 없으면 0으로 표시)
  const inventoryWithMaterials: MaterialInventoryDto[] = React.useMemo(() => {
    return materials.map((material) => {
      const existingInventory = inventoryList.find((inv) => inv.materialId === material.id);
      if (existingInventory) {
        return {
          ...existingInventory,
          materialName: existingInventory.materialName || material.name,
        };
      }
      // 재고가 없는 자재는 0으로 표시
      return {
        id: 0,
        materialId: material.id,
        materialName: material.name,
        quantity: 0,
        updatedAt: new Date().toISOString(),
      };
    });
  }, [materials, inventoryList]);

  // 필터링된 재고 목록
  const filteredInventory = inventoryWithMaterials.filter((inv) => {
    const material = materials.find((m) => m.id === inv.materialId);
    if (!material) return false;

    // 타입 필터
    if (typeFilter !== "ALL" && material.type !== typeFilter) return false;

    // 검색어 필터
    const materialName = inv.materialName || material.name;
    if (searchKeyword && !materialName.toLowerCase().includes(searchKeyword.toLowerCase())) {
      return false;
    }

    return true;
  });

  const handleInventoryChange = async (
    type: "RECEIVE" | "DEDUCT" | "RETURN",
    materialId: number,
    request: InventoryRequest,
  ) => {
    try {
      let result: { data: MaterialInventoryDto | null; errorMsg: string | null; errorCode: number | null };
      if (type === "RECEIVE") {
        result = await companyProductSystemApiService.receiveInventory(cid, materialId, request);
      } else if (type === "RETURN") {
        result = await companyProductSystemApiService.returnInventory(cid, materialId, request);
      } else {
        result = await companyProductSystemApiService.deductInventory(cid, materialId, request);
      }

      // API 응답으로 상태 업데이트
      if (result.data) {
        const newInventory = result.data;
        setInventoryList((prev) => {
          const exists = prev.some((inv) => inv.materialId === materialId);
          if (exists) {
            // 기존 재고 업데이트
            return prev.map((inv) =>
              inv.materialId === materialId ? newInventory : inv
            );
          } else {
            // 새 재고 추가
            return [...prev, newInventory];
          }
        });
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const getMaterialName = (inv: MaterialInventoryDto): string => {
    if (inv.materialName) return inv.materialName;
    const material = materials.find((m) => m.id === inv.materialId);
    return material?.name || `자재 #${inv.materialId}`;
  };

  const openChangeModal = (
    type: "RECEIVE" | "DEDUCT" | "RETURN",
    inv: MaterialInventoryDto,
  ) => {
    setChangeModal({
      isOpen: true,
      type,
      materialId: inv.materialId,
      materialName: getMaterialName(inv),
      currentQuantity: inv.quantity,
    });
  };

  const openLogModal = async (inv: MaterialInventoryDto) => {
    try {
      const res = await companyProductSystemApiService.getInventoryLogs(cid, inv.materialId);
      setLogModal({
        isOpen: true,
        materialId: inv.materialId,
        materialName: getMaterialName(inv),
        logs: res.data?.content || [],
      });
    } catch (e) {
      console.error(e);
      alert("이력을 불러오지 못했습니다.");
    }
  };

  const MATERIAL_TYPES = [
    { key: "ALL", label: "전체" },
    { key: "SHEET_PAPER", label: "낱장 용지" },
    { key: "ROLL_PAPER", label: "롤 용지" },
    { key: "INK", label: "잉크" },
    { key: "INK_CONFIG", label: "인쇄 구성" },
    { key: "OTHER", label: "기타" },
  ];

  return (
    <CompanyAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-[#222]" />
            <h1 className="text-[22px] font-bold text-[#222]">재고 관리</h1>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#999]" />
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="자재명 검색"
              className="w-full pl-10 pr-4 py-2.5 border border-[#e0e0e0] rounded-[8px] text-[14px] focus:border-[#4285F4] focus:outline-none"
            />
          </div>
        </div>

        {/* Type Filter Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#eee] overflow-x-auto">
          {MATERIAL_TYPES.map((mt) => (
            <button
              key={mt.key}
              onClick={() => setTypeFilter(mt.key)}
              className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                typeFilter === mt.key
                  ? "border-[#222] text-[#222]"
                  : "border-transparent text-[#999] hover:text-[#555]"
              }`}
            >
              {mt.label}
            </button>
          ))}
        </div>

        {/* Inventory Table */}
        {loading ? (
          <div className="py-10 text-center text-[14px] text-[#999]">불러오는 중...</div>
        ) : filteredInventory.length === 0 ? (
          <div className="py-10 text-center text-[14px] text-[#bbb]">
            {searchKeyword || typeFilter !== "ALL" ? "검색 결과가 없습니다." : "등록된 재고가 없습니다."}
          </div>
        ) : (
          <div className="bg-white border border-[#eee] rounded-[12px] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#fafafa] border-b border-[#eee]">
                <tr>
                  <th className="px-5 py-4 text-[12px] font-medium text-[#888] text-left">자재명</th>
                  <th className="px-5 py-4 text-[12px] font-medium text-[#888] text-center">유형</th>
                  <th className="px-5 py-4 text-[12px] font-medium text-[#888] text-right">현재 재고</th>
                  <th className="px-5 py-4 text-[12px] font-medium text-[#888] text-center">최근 업데이트</th>
                  <th className="px-5 py-4 text-[12px] font-medium text-[#888] text-center">관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.map((inv) => {
                  const material = materials.find((m) => m.id === inv.materialId);
                  const badgeColor = material
                    ? TYPE_BADGE_COLORS[material.type] || TYPE_BADGE_COLORS.OTHER
                    : TYPE_BADGE_COLORS.OTHER;

                  return (
                    <tr key={inv.materialId} className="border-b border-[#f0f0f0] hover:bg-[#fafafa]">
                      <td className="px-5 py-4">
                        <span className="text-[14px] font-medium text-[#222]">{inv.materialName}</span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span
                          className="px-2.5 py-1 text-[11px] font-medium rounded-[4px]"
                          style={{ backgroundColor: badgeColor.bg, color: badgeColor.text }}
                        >
                          {material ? MATERIAL_TYPE_LABELS[material.type] || material.type : "-"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className={`text-[16px] font-bold ${inv.quantity < 100 ? "text-[#999]" : "text-[#222]"}`}>
                          {inv.quantity.toLocaleString()}
                        </span>
                        <span className="text-[12px] text-[#888] ml-1">개</span>
                      </td>
                      <td className="px-5 py-4 text-center text-[13px] text-[#888]">
                        {new Date(inv.updatedAt).toLocaleDateString("ko-KR")}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openChangeModal("RECEIVE", inv)}
                            className="px-2.5 py-1 text-[12px] text-[#555] hover:text-[#222] hover:bg-[#f5f5f5] rounded-[4px] transition-colors"
                          >
                            입고
                          </button>
                          <button
                            onClick={() => openChangeModal("DEDUCT", inv)}
                            className="px-2.5 py-1 text-[12px] text-[#555] hover:text-[#222] hover:bg-[#f5f5f5] rounded-[4px] transition-colors"
                          >
                            차감
                          </button>
                          <button
                            onClick={() => openChangeModal("RETURN", inv)}
                            className="px-2.5 py-1 text-[12px] text-[#555] hover:text-[#222] hover:bg-[#f5f5f5] rounded-[4px] transition-colors"
                          >
                            반품
                          </button>
                          <button
                            onClick={() => openLogModal(inv)}
                            className="px-2.5 py-1 text-[12px] text-[#555] hover:text-[#222] hover:bg-[#f5f5f5] rounded-[4px] transition-colors"
                          >
                            이력
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Modals */}
        {changeModal && (
          <InventoryChangeModal
            isOpen={changeModal.isOpen}
            onClose={() => setChangeModal(null)}
            onSubmit={(request) =>
              handleInventoryChange(changeModal.type, changeModal.materialId, request)
            }
            type={changeModal.type}
            materialName={changeModal.materialName}
            currentQuantity={changeModal.currentQuantity}
          />
        )}

        {logModal && (
          <InventoryLogModal
            isOpen={logModal.isOpen}
            onClose={() => setLogModal(null)}
            materialName={logModal.materialName}
            materialId={logModal.materialId}
            companyId={cid}
            logs={logModal.logs}
            onUpdate={async () => {
              // 이력 새로고침
              const res = await companyProductSystemApiService.getInventoryLogs(cid, logModal.materialId);
              setLogModal((prev) => prev ? { ...prev, logs: res.data?.content || [] } : null);
              // 재고 목록도 새로고침
              loadData();
            }}
          />
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default InventoryManagementPage;
